import React, { useMemo } from 'react'
import { CobuStack, CobuText, CobuImage, CobuCard } from '@cobu/ui'
import { Strings } from '../../constants/Strings';

type GroupMeta = { groupMemberCount?: number; upcomingGroupEventCount?: number }
type Group = { uuid: string; name: string; imageUrl?: string; meta?: GroupMeta }

type Props = {
  groups: Group[]
  title: string
  onGroupPress?: (group: Group) => void
}

export default function PublicProfileGroups({ groups, title, onGroupPress }: Props) {
  const uniqueGroups = useMemo(() => {
    if (!groups) return [] as Group[]
    return groups.filter((g, i, a) => a.findIndex(t => t.uuid === g.uuid) === i)
  }, [groups])

  if (!uniqueGroups || uniqueGroups.length === 0) return null

  return (
    <CobuCard bc="$white" mb="$2" p="$0">
      <CobuStack pt="$4">
        <CobuText variant="heading" col="$cobuDarkBlue" mb="$2" px="$4" style={{ fontSize: 21 as any }}>
          {title}
        </CobuText>

        <div style={{ overflowX: 'auto' }}>
          <div style={{ display: 'flex', gap: 12, paddingBottom: 16 }}>
            {uniqueGroups.map((group) => (
              <div
                key={group.uuid}
                onClick={() => onGroupPress?.(group)}
                role="button"
                tabIndex={0}
                style={{
                  paddingTop: 12,
                  paddingLeft: 16,
                  boxShadow: '0 2px 8px rgba(128,128,128,0.1)',
                  cursor: 'pointer',
                }}
              >
                <div style={{ position: 'relative', width: 132, height: 156, borderRadius: 10, overflow: 'hidden' }}>
                  <CobuImage
                    source={{ uri: group.imageUrl || 'https://www.flynz.co.nz/wp-content/uploads/profile-placeholder.png' }}
                    containerProps={{ w: 132, h: 156, br: 10, ov: 'hidden' }}
                    style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 } as any}
                  />
                  <div
                    style={{
                      position: 'absolute', inset: 0,
                      background: 'linear-gradient(180deg, transparent 0%, var(--cobuDarkBlue) 100%)',
                      padding: 8,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                    }}
                  >
                    <CobuText variant="body" fos={'$3' as any} fow={'500' as any} col="$white" numberOfLines={2 as any}>
                      {group.name}
                    </CobuText>

                    <CobuStack>
                      {group.meta?.groupMemberCount ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden>
                            <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5s-3 1.34-3 3 1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.67 0-8 1.34-8 4v2h10v-2c0-1.3.84-2.4 2.05-3.16C11.19 12.93 9.65 13 8 13zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.95 1.97 3.45v2h7v-2c0-2.66-5.33-4-8-4z" fill="var(--cobuTeal)" />
                          </svg>
                          <CobuText variant="caption" fos={'$2' as any} col="$white" fow={'500' as any}>
                            {Strings.format(group.meta.groupMemberCount === 1 ? Strings.get().groups.card.memberCount.singular : Strings.get().groups.card.memberCount.plural, { count: String(group.meta.groupMemberCount) })}
                          </CobuText>
                        </div>
                      ) : null}
                      {group.meta?.upcomingGroupEventCount ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden>
                            <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z" fill="var(--cobuTeal)" />
                          </svg>
                          <CobuText variant="caption" fos={'$2' as any} col="$white" fow={'500' as any}>
                            {Strings.format(group.meta.upcomingGroupEventCount === 1 ? Strings.get().groups.card.eventCount.singular : Strings.get().groups.card.eventCount.plural, { count: String(group.meta.upcomingGroupEventCount) })}
                          </CobuText>
                        </div>
                      ) : null}
                    </CobuStack>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </CobuStack>
    </CobuCard>
  )
}


